import { cn, getStatusColor } from "@/lib/utils";

const sizeClassMap = {
  xs: "h-1.5 w-1.5",
  sm: "h-2 w-2",
  md: "h-2.5 w-2.5",
  lg: "h-3 w-3",
} as const;

export type StatusDotSize = keyof typeof sizeClassMap;

export interface StatusDotProps<T extends string = string> {
  status: T;
  /**
   * Optional mapping of statuses to semantic background classes.
   * Defaults to the shared getStatusColor helper.
   */
  colorMap?: Record<T, string>;
  size?: StatusDotSize;
  className?: string;
  /**
   * Optional label announced by assistive tech. Defaults to the status name.
   */
  ariaLabel?: string;
  /**
   * Whether to animate the dot with a pulse to highlight activity.
   */
  pulse?: boolean;
}

export function StatusDot<T extends string = string>({
  status,
  colorMap,
  size = "sm",
  className,
  ariaLabel,
  pulse = false,
}: StatusDotProps<T>) {
  const backgroundClass = getStatusColor(status, colorMap);
  const sizeClass = sizeClassMap[size];

  return (
    <span
      aria-label={ariaLabel ?? status}
      className={cn(
        "inline-flex shrink-0 rounded-full",
        sizeClass,
        backgroundClass,
        pulse && "animate-pulse",
        className,
      )}
      role="status"
    />
  );
}
